import * as React from 'react';
import { cn } from '@/lib/utils';
import DOMPurify from 'dompurify'

interface ContentPreviewProps extends React.HTMLAttributes<HTMLDivElement> {
  content: string;
  maxHeight?: string;
  showBorder?: boolean;
}

const ContentPreview = React.forwardRef<HTMLDivElement, ContentPreviewProps>(
  ({
    content,
    className,
    maxHeight = '400px',
    showBorder = true,
    ...props
  }, ref) => {
    // Sanitize incoming HTML content to prevent XSS
    const safeHtml = React.useMemo(() => {
      try {
        // Allow a conservative set of tags/attributes used by the editor preview
        return DOMPurify.sanitize(content || '', {
          USE_PROFILES: { html: true },
        }) || '<p class="text-gray-400 italic">No content to preview</p>'
      } catch {
        return '<p class="text-gray-400 italic">No content to preview</p>'
      }
    }, [content])
    return (
      <div
        ref={ref}
        className={cn(
          'prose prose-sm max-w-none overflow-auto p-4 bg-white',
          showBorder && 'border border-gray-200 rounded-lg',
          className
        )}
        style={{ maxHeight }}
        {...props}
      >
      <div 
        dangerouslySetInnerHTML={{ __html: safeHtml }}
        className="prose-headings:font-semibold prose-h1:text-2xl prose-h2:text-xl prose-h3:text-lg prose-p:text-sm prose-li:text-sm prose-a:text-primary prose-img:rounded-lg prose-img:shadow-sm"
      />
      
      {/* Custom styles for content preview */}
      <style>{`
        .callout {
          background-color: hsl(var(--primary) / 0.12) !important;
          border-left: 4px solid hsl(var(--primary)) !important;
          padding: 1rem !important;
          margin: 1rem 0 !important;
          border-radius: 0.375rem !important;
        }
      `}</style>
    </div>
  );
}
)
ContentPreview.displayName = "ContentPreview"

export { ContentPreview }
